import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import SchoolEmployeesEditor from '../components/SchoolEmployeesEditor'
import SchoolEmployeeDetailModal from '../components/SchoolEmployeeDetailModal'
import { useAuth } from '../hooks/useAuth'
import { highlightTextMultiple } from '../utils/textHighlight'
import {
  formatBirthDateDisplay,
  formatEmployeeFullName,
  getEmployeeInitials,
  getEmployeePositionLabel,
} from '../utils/employeeDisplay'
import { comparePersonNames } from '../utils/personNameSort'
import './SchoolEmployees.css'

interface Employee {
  id: string
  name: string
  title?: string
  first_name?: string
  last_name?: string
  title_after?: string
  email?: string
  work_email?: string
  personal_number?: string
  birth_date?: string
  phone?: string
  role?: string
  position?: string
}

function canViewFullSchoolEmployeeData(user: { role?: string | null; admin_type?: string | null } | null): boolean {
  if (!user) return false
  if (user.role === 'personalista') return true
  if (user.role === 'admin' && user.admin_type === 'spravce_it') return true
  return (
    ['director', 'zastupce_reditele', 'financist', 'referent_eko', 'vedouci'].includes(user.role || '')
  )
}

export default function SchoolEmployees() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const canManageEmployees = useMemo(
    () =>
      Boolean(
        user &&
          (user.role === 'personalista' ||
            (user.role === 'admin' && user.admin_type === 'spravce_it')),
      ),
    [user],
  )
  const canViewFullData = useMemo(() => canViewFullSchoolEmployeeData(user), [user])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [manageMode, setManageMode] = useState(false)

  const showListView = !canManageEmployees || !manageMode

  const openEmployeeDetail = (employee: Employee) => {
    setSelectedEmployee(employee)
  }

  useEffect(() => {
    if (authLoading || !showListView) {
      return
    }
    loadEmployees()
  }, [authLoading, showListView, canViewFullData, user?.id])

  const loadEmployees = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || window.location.origin + '/api'
      
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }

      const response = await fetch(`${apiUrl}/employees`, {
        method: 'GET',
        headers,
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        const dbEmployees: Employee[] = Array.isArray(data) ? data.map((emp: any) => ({
          id: emp.id?.toString() || emp.name,
          name: emp.name || '',
          title: emp.title || undefined,
          first_name: emp.first_name || undefined,
          last_name: emp.last_name || undefined,
          title_after: (emp.title_after !== null && emp.title_after !== undefined) ? emp.title_after : undefined,
          email: emp.work_email || emp.email || '',
          work_email: emp.work_email || emp.email || undefined,
          personal_number: canViewFullData ? (emp.personal_number || undefined) : undefined,
          birth_date: canViewFullData ? (emp.birth_date || undefined) : undefined,
          phone: emp.phone || undefined,
          role: emp.role || undefined,
          position: emp.position || undefined
        })) : []
        
        setEmployees(dbEmployees)
        if (dbEmployees.length === 0) {
          console.warn('Employees list is empty')
        }
      } else {
        // Более детальная обработка ошибок
        const errorText = await response.text()
        let errorMessage = 'Nepodařilo se načíst seznam zaměstnanců'
        
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.error || errorData.message || errorMessage
        } catch {
          if (response.status === 401) {
            errorMessage = 'API vyžaduje autorizaci. Kontaktujte administrátora.'
          } else if (response.status === 403) {
            errorMessage = 'Přístup odepřen'
          } else if (response.status === 404) {
            errorMessage = 'Endpoint nebyl nalezen'
          } else {
            errorMessage = `Chyba serveru: ${response.status}`
          }
        }
        
        console.error('Failed to load employees:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        })
        
        throw new Error(errorMessage)
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Chyba při načítání zaměstnanců'
      setError(errorMessage)
      console.error('Error loading employees:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredEmployees = useMemo(() => {
    const search = searchTerm.toLowerCase()
    const list = !searchTerm
      ? [...employees]
      : employees.filter(
          (emp) =>
            (emp.name && emp.name.toLowerCase().includes(search)) ||
            (emp.first_name && emp.first_name.toLowerCase().includes(search)) ||
            (emp.last_name && emp.last_name.toLowerCase().includes(search)) ||
            (emp.title && emp.title.toLowerCase().includes(search)) ||
            (emp.title_after && emp.title_after.toLowerCase().includes(search)) ||
            (emp.email && emp.email.toLowerCase().includes(search)) ||
            (emp.personal_number && emp.personal_number.includes(searchTerm)) ||
            (emp.birth_date && formatBirthDateDisplay(emp.birth_date).includes(searchTerm)) ||
            (emp.phone && emp.phone.includes(searchTerm)) ||
            (emp.position && emp.position.toLowerCase().includes(search)),
        )
    list.sort((a, b) =>
      comparePersonNames(
        { name: a.name, first_name: a.first_name, last_name: a.last_name },
        { name: b.name, first_name: b.first_name, last_name: b.last_name },
      ),
    )
    return list
  }, [employees, searchTerm])

  const handleExportPDF = () => {
    // Используем отфильтрованных сотрудников (filteredEmployees уже учитывает пустой поиск)
    const employeesToPrint = filteredEmployees
    
    console.log('PDF Export:', {
      searchTerm,
      employeesCount: employees.length,
      filteredCount: filteredEmployees.length,
      toPrintCount: employeesToPrint.length,
      employeesData: employeesToPrint.slice(0, 3) // Первые 3 для отладки
    })
    
    if (employeesToPrint.length === 0) {
      alert('Žádní zaměstnanci k tisku')
      return
    }
    
    // Создаем контейнер для печати - используем другой подход
    const printContainer = document.createElement('div')
    printContainer.className = 'print-container'
    printContainer.style.cssText = 'position: fixed; top: 0; left: 0; width: 210mm; min-height: 100vh; background: white; z-index: 99999; padding: 0; margin: 0; visibility: hidden;'
    
    // Создаем заголовок с логотипом и информацией о школе
    const header = document.createElement('div')
    header.className = 'print-header'
    
    // Логотип
    const logoContainer = document.createElement('div')
    logoContainer.className = 'print-logo-container'
    const logo = document.createElement('img')
    logo.src = '/logo.png'
    logo.alt = 'ISŠ Cheb Logo'
    logo.className = 'print-logo'
    logo.style.cssText = 'max-height: 45px; width: auto;'
    logo.onerror = function() {
      this.src = '/logo.png'
    }
    logoContainer.appendChild(logo)
    
    // Информация о школе
    const schoolInfo = document.createElement('div')
    schoolInfo.className = 'print-school-info'
    const schoolName = document.createElement('div')
    schoolName.className = 'print-school-name'
    schoolName.textContent = 'Integrovaná střední škola Cheb, příspěvková organizace'
    const schoolAddress = document.createElement('div')
    schoolAddress.className = 'print-school-address'
    schoolAddress.textContent = 'Obměné brigády 2258/6, 350 02 Cheb'
    
    schoolInfo.appendChild(schoolName)
    schoolInfo.appendChild(schoolAddress)
    
    header.appendChild(logoContainer)
    header.appendChild(schoolInfo)
    printContainer.appendChild(header)
    
    // Создаем заголовок документа
    const title = document.createElement('h1')
    title.textContent = 'Seznam zaměstnanců školy'
    title.className = 'print-title'
    printContainer.appendChild(title)
    
    // Создаем таблицу
    const table = document.createElement('table')
    table.className = 'print-table'
    
    // Заголовки колонок
    const thead = document.createElement('thead')
    const headerRow = document.createElement('tr')
    const headers = ['Jméno', 'Telefon', 'Email', 'Pozice']
    headers.forEach(headerText => {
      const th = document.createElement('th')
      th.textContent = headerText
      headerRow.appendChild(th)
    })
    thead.appendChild(headerRow)
    table.appendChild(thead)
    
    // Тело таблицы
    const tbody = document.createElement('tbody')
    employeesToPrint.forEach(emp => {
      const row = document.createElement('tr')
      
      const fullName = formatEmployeeFullName(emp)
      
      // Создаем ячейки с правильными классами
      const cells = [
        { text: fullName, className: '' },
        { text: emp.phone || '-', className: 'phone-cell' },
        { text: emp.email || '-', className: '' },
        { text: emp.position || emp.role || '-', className: '' }
      ]
      
      cells.forEach((cell, index) => {
        const td = document.createElement('td')
        td.textContent = cell.text
        if (cell.className) {
          td.className = cell.className
        }
        // Для колонки телефона (индекс 1) добавляем nowrap
        if (index === 1) {
          td.style.whiteSpace = 'nowrap'
        }
        row.appendChild(td)
      })
      
      tbody.appendChild(row)
    })
    table.appendChild(tbody)
    printContainer.appendChild(table)
    
    // Добавляем в DOM
    document.body.appendChild(printContainer)
    
    // Сохраняем оригинальное название страницы
    const originalTitle = document.title
    document.title = `Seznam zaměstnanců školy - ${new Date().toISOString().split('T')[0]}`
    
    // Проверяем содержимое перед печатью
    const rowsCount = printContainer.querySelectorAll('tbody tr').length
    console.log('Print container rows:', rowsCount)
    
    if (rowsCount === 0) {
      alert('Chyba: Tabulka je prázdná. Zkuste znovu načíst stránku.')
      document.body.removeChild(printContainer)
      document.title = originalTitle
      return
    }
    
    // Делаем контейнер видимым и правильно позиционируем перед печатью
    setTimeout(() => {
      // Перемещаем в начало body и делаем видимым
      document.body.insertBefore(printContainer, document.body.firstChild)
      printContainer.style.visibility = 'visible'
      printContainer.style.position = 'static'
      printContainer.style.left = 'auto'
      printContainer.style.top = 'auto'
      printContainer.style.width = '100%'
      
      // Принудительный reflow
      printContainer.offsetHeight
      
      // Печать
      window.print()
    }, 100)
    
    // Удаляем контейнер и восстанавливаем название после печати
    const cleanup = () => {
      if (document.body.contains(printContainer)) {
        document.body.removeChild(printContainer)
      }
      document.title = originalTitle
    }
    
    // Ожидаем завершения печати
    window.addEventListener('afterprint', cleanup, { once: true })
    // Также очищаем через 5 секунд на случай, если событие afterprint не сработает
    setTimeout(cleanup, 5000)
  }

  const handleExportGoogleContacts = () => {
    // Формат CSV для импорта в Google Contacts
    const headers = [
      'Name',
      'Given Name',
      'Family Name',
      'E-mail 1 - Type',
      'E-mail 1 - Value',
      'Phone 1 - Type',
      'Phone 1 - Value',
      'Organization 1 - Type',
      'Organization 1 - Name',
      'Organization 1 - Title'
    ]
    
    const rows = filteredEmployees.map(emp => {
      const givenName = emp.first_name || ''
      const familyName = emp.last_name || ''
      const fullName = formatEmployeeFullName(emp)
      
      return [
        fullName,                    // Name
        givenName,                  // Given Name
        familyName,                 // Family Name
        'Work',                      // E-mail 1 - Type
        emp.email || '',            // E-mail 1 - Value
        'Work',                      // Phone 1 - Type
        emp.phone || '',            // Phone 1 - Value
        'Work',                      // Organization 1 - Type
        'ISS Cheb',                 // Organization 1 - Name
        emp.position || emp.role || '' // Organization 1 - Title
      ]
    })
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `google_contacts_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="school-employees">
      <Header
        leftButtons={
          <button
            onClick={() => navigate(-1)}
            className="back-button"
            type="button"
          >
            ← Zpět
          </button>
        }
        showLogo={true}
      />
      <div className={`employees-container${canViewFullData ? ' employees-container--privileged' : ''}`}>
        <div className="employees-content">
          <header className="employees-page-header">
            <div className="employees-page-header__row">
              <div>
                <h1 className="employees-page-title">Seznam zaměstnanců školy</h1>
                <p className="employees-page-subtitle">
                  Kontakty a osobní čísla zaměstnanců ISŠ Cheb
                </p>
              </div>
              {canManageEmployees && (
                <div className="employees-mode-switch">
                  {manageMode ? (
                    <button
                      type="button"
                      className="employees-mode-btn employees-mode-btn--back"
                      onClick={() => setManageMode(false)}
                    >
                      ← Seznam zaměstnanců
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="employees-mode-btn employees-mode-btn--manage"
                      onClick={() => setManageMode(true)}
                    >
                      Správa seznamu
                    </button>
                  )}
                </div>
              )}
            </div>
          </header>
          {canManageEmployees && manageMode ? (
            <SchoolEmployeesEditor
              requireUnlockForMutations={user?.role === 'personalista'}
            />
          ) : authLoading ? (
            <div className="loading">Načítání...</div>
          ) : loading ? (
            <div className="loading">Načítání...</div>
          ) : error ? (
            <div className="error-message">❌ {error}</div>
          ) : (
            <>
              <div className="employees-toolbar">
                <div className="employees-toolbar__search">
                  <span className="employees-toolbar__search-icon" aria-hidden>⌕</span>
                  <input
                    type="search"
                    placeholder="Hledat jméno, e-mail, tel. nebo os. č.…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                    enterKeyHint="search"
                    autoComplete="off"
                  />
                </div>
                <div className="employees-toolbar__meta">
                  <span className="employees-count-badge">
                    {searchTerm
                      ? `${filteredEmployees.length} / ${employees.length}`
                      : employees.length}{' '}
                    zaměstnanců
                  </span>
                  <div className="export-buttons">
                    <button
                      onClick={handleExportPDF}
                      className="export-button pdf-button"
                      title="Vytvořit PDF pro tisk"
                      type="button"
                    >
                      PDF pro tisk
                    </button>
                    <button
                      onClick={handleExportGoogleContacts}
                      className="export-button google-contacts-button"
                      title="Exportovat do Google Contacts"
                      type="button"
                    >
                      Google Contacts
                    </button>
                  </div>
                </div>
              </div>

              {canViewFullData && (
                <p className="employees-privileged-hint">
                  Klikněte na zaměstnance pro zobrazení detailu včetně data narození a kontaktního e-mailu.
                </p>
              )}

              <div className="employees-table-container">
                {filteredEmployees.length === 0 ? (
                  <div className="no-results">
                    {searchTerm ? 'Žádní zaměstnanci neodpovídají vyhledávání' : 'Žádní zaměstnanci nenalezeni'}
                  </div>
                ) : (
                  <>
                    <div className="employees-table-scroll">
                      <table className={`employees-table employees-table-desktop${canViewFullData ? ' employees-table--privileged' : ''}`}>
                        <thead>
                          <tr>
                            <th className="col-avatar" aria-hidden />
                            <th>Zaměstnanec</th>
                            {canViewFullData && <th className="col-os">Os. č.</th>}
                            <th className="col-phone">Telefon</th>
                            <th className="col-email">E-mail</th>
                            {!canViewFullData && <th>Pozice</th>}
                            {!canViewFullData && <th className="col-actions">Kontakt</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredEmployees.map((employee) => {
                            const fullName = formatEmployeeFullName(employee)
                            const positionLabel = getEmployeePositionLabel(employee)
                            const initials = getEmployeeInitials(employee)
                            return (
                              <tr
                                key={employee.id}
                                className={canViewFullData ? 'employees-table-row--clickable' : undefined}
                                onClick={canViewFullData ? () => openEmployeeDetail(employee) : undefined}
                                onKeyDown={
                                  canViewFullData
                                    ? (e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault()
                                          openEmployeeDetail(employee)
                                        }
                                      }
                                    : undefined
                                }
                                tabIndex={canViewFullData ? 0 : undefined}
                                role={canViewFullData ? 'button' : undefined}
                                aria-label={canViewFullData ? `Detail: ${fullName}` : undefined}
                              >
                                <td className="col-avatar">
                                  <span className="employee-avatar" aria-hidden>
                                    {initials}
                                  </span>
                                </td>
                                <td className="employee-name-cell">
                                  <span className="employee-full-name">
                                    {highlightTextMultiple(fullName, searchTerm)}
                                  </span>
                                  {canViewFullData && positionLabel && (
                                    <span className="employee-position-sub" title={positionLabel}>
                                      {highlightTextMultiple(positionLabel, searchTerm)}
                                    </span>
                                  )}
                                </td>
                                {canViewFullData && (
                                  <td className="col-os employee-os">
                                    {employee.personal_number
                                      ? highlightTextMultiple(employee.personal_number, searchTerm)
                                      : <span className="cell-muted">—</span>}
                                  </td>
                                )}
                                <td className="employee-phone">
                                  {employee.phone ? (
                                    <a
                                      href={`tel:${employee.phone}`}
                                      className="contact-link"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {highlightTextMultiple(employee.phone, searchTerm)}
                                    </a>
                                  ) : (
                                    <span className="cell-muted">—</span>
                                  )}
                                </td>
                                <td className="employee-email">
                                  {employee.email ? (
                                    <a
                                      href={`mailto:${employee.email}`}
                                      className="contact-link contact-link--email"
                                      title={employee.email}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {highlightTextMultiple(employee.email, searchTerm)}
                                    </a>
                                  ) : (
                                    <span className="cell-muted">—</span>
                                  )}
                                </td>
                                {!canViewFullData && (
                                  <td className="employee-role">
                                    {positionLabel
                                      ? highlightTextMultiple(positionLabel, searchTerm)
                                      : <span className="cell-muted">—</span>}
                                  </td>
                                )}
                                {!canViewFullData && (
                                  <td className="employee-actions col-actions">
                                    <div className="action-group">
                                      {employee.phone && (
                                        <a
                                          href={`tel:${employee.phone}`}
                                          className="action-chip action-chip--phone"
                                          title="Zavolat"
                                        >
                                          Tel
                                        </a>
                                      )}
                                      {employee.email && (
                                        <a
                                          href={`mailto:${employee.email}`}
                                          className="action-chip action-chip--email"
                                          title="Napsat e-mail"
                                        >
                                          Mail
                                        </a>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="employees-mobile">
                      {filteredEmployees.map((employee) => {
                        const fullName = formatEmployeeFullName(employee)
                        const positionLabel = getEmployeePositionLabel(employee)
                        const initials = getEmployeeInitials(employee)
                        return (
                          <article
                            key={employee.id}
                            className={`employee-card-mobile${canViewFullData ? ' employee-card-mobile--clickable' : ''}`}
                            onClick={canViewFullData ? () => openEmployeeDetail(employee) : undefined}
                            onKeyDown={
                              canViewFullData
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault()
                                      openEmployeeDetail(employee)
                                    }
                                  }
                                : undefined
                            }
                            tabIndex={canViewFullData ? 0 : undefined}
                            role={canViewFullData ? 'button' : undefined}
                          >
                            <div className="employee-card-main">
                              <span className="employee-avatar employee-avatar--mobile" aria-hidden>
                                {initials}
                              </span>
                              <div className="employee-card-body">
                                <h2 className="employee-name-mobile">
                                  {highlightTextMultiple(fullName, searchTerm)}
                                </h2>
                                {positionLabel && (
                                  <p className="employee-position-mobile">
                                    {highlightTextMultiple(positionLabel, searchTerm)}
                                  </p>
                                )}
                                {canViewFullData && employee.personal_number && (
                                  <p className="employee-os-mobile">
                                    Os. č.{' '}
                                    {highlightTextMultiple(employee.personal_number, searchTerm)}
                                  </p>
                                )}
                                {canViewFullData && employee.birth_date && (
                                  <p className="employee-birth-mobile">
                                    Narození{' '}
                                    {highlightTextMultiple(formatBirthDateDisplay(employee.birth_date), searchTerm)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div
                              className="employee-card-contact-row"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {employee.phone ? (
                                <a
                                  href={`tel:${employee.phone}`}
                                  className="mobile-contact-btn mobile-contact-btn--phone"
                                >
                                  <span className="mobile-contact-btn__label">Zavolat</span>
                                  <span className="mobile-contact-btn__value">
                                    {highlightTextMultiple(employee.phone, searchTerm)}
                                  </span>
                                </a>
                              ) : null}
                              {employee.email ? (
                                <a
                                  href={`mailto:${employee.email}`}
                                  className="mobile-contact-btn mobile-contact-btn--email"
                                >
                                  <span className="mobile-contact-btn__label">Prac. e-mail</span>
                                  <span className="mobile-contact-btn__value">
                                    {highlightTextMultiple(employee.email, searchTerm)}
                                  </span>
                                </a>
                              ) : null}
                              {!employee.phone && !employee.email && (
                                <p className="employee-card-no-contact">Kontakt není uveden</p>
                              )}
                            </div>
                          </article>
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <SchoolEmployeeDetailModal
        employee={selectedEmployee}
        isOpen={Boolean(canViewFullData && selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
      />
    </div>
  )
}

